import { useState, useEffect } from "react";
import {
  Card,
  Title,
  Button,
  Text,
  TextInput,
} from "@tremor/react";
import DeviceDataTable from "../../components/DeviceDataTable";
import DeviceStatsCards from "../../components/DeviceStatsCards";
import Nav from "../../components/nav";
import * as devicesAPI from "../../services/device";
import { CpuChipIcon, PlusCircleIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

// Skeleton for data view table
function DeviceDataTableSkeleton() {
  return (
    <div className="animate-pulse overflow-x-auto">
      {/* Table Header */}
      <div className="flex items-center p-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex-1 grid grid-cols-3 gap-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded min-w-[200px]"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded min-w-[100px]"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded min-w-[300px]"></div>
        </div>
      </div>

      {/* Table Rows */}
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex items-center p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex-1 grid grid-cols-3 gap-4 items-center">
            {/* Device */}
            <div className="space-y-2 min-w-[200px]">
              <div className="h-5 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
              <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
              <div className="h-5 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
            </div>

            {/* Status */}
            <div className="space-y-2 min-w-[100px]">
              <div className="h-5 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
              <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-20"></div>
            </div>

            {/* Latest Values */}
            <div className="space-y-2 min-w-[300px]">
              <div className="flex gap-2">
                <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-20"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-24"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-18"></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const EmptyDevice = function ({ open }) {
  return (
    <>
      <div className="mt-4 flex h-52 items-center justify-center rounded-tremor-small border border-dashed border-tremor-border p-4 dark:border-dark-tremor-border">
        <div className="text-center">
          <CpuChipIcon
            className="mx-auto h-7 w-7 text-tremor-content-subtle dark:text-dark-tremor-content-subtle"
            aria-hidden={true}
          />

          <p className="mt-2 text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            No Devices available
          </p>
          <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
            Get started by connecting a device
          </p>
          <Button
            icon={PlusCircleIcon}
            onClick={open}
            type="button"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-tremor-small bg-tremor-brand px-3 py-2 text-tremor-default font-medium text-tremor-brand-inverted shadow-tremor-input hover:bg-tremor-brand-emphasis"
          >
            Add Device
          </Button>
        </div>
      </div>
    </>
  );
};

export default function Devices() {
  let [devices, setDevices] = useState([]);
  let [loading, setLoading] = useState(true);
  let [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const getDevices = async () => {
      setLoading(true);
      try {
        const data = await devicesAPI.fetchDevices();
        setDevices(data);
      } catch (err) {
        console.error("Error fetching devices:", err);
      } finally {
        setLoading(false);
      }
    };

    getDevices();
  }, []);

  const filteredDevices = searchQuery
    ? devices.filter(device => {
        const query = searchQuery.toLowerCase();
        return (
          (device.name && device.name.toLowerCase().includes(query)) ||
          (device.serial && device.serial.toLowerCase().includes(query))
        );
      })
    : devices;

  return (
    <div>
      <Nav />
      <main className="p-4 md:p-10 mx-auto max-w-7xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <Title>Devices</Title>
            <Text>Devices available to connect to the network.</Text>
          </div>
          <div className="flex items-center gap-2">
            <TextInput
              icon={MagnifyingGlassIcon}
              placeholder="Search by name or serial..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
            />
            <Button variant="primary" size="xs" onClick={() => navigate("/devices/new")} className="whitespace-nowrap">
              Add Device
            </Button>
          </div>
        </div>

        {devices.length > 0 && (
          <DeviceStatsCards devices={devices} />
        )}

        <Card className="mt-6">
          {loading ? (
            <DeviceDataTableSkeleton />
          ) : devices.length > 0 ? (
            <DeviceDataTable devices={filteredDevices} />
          ) : (
            <EmptyDevice open={() => navigate("/devices/new")} />
          )}
        </Card>
      </main>
    </div>
  );
}
